/**
 * The grass-and-skyline background the account page, the sign-up questions and
 * a shared schedule are drawn on, and the numbers that place it.
 *
 * Both files fade from a near-white sky through a gradient into one flat green.
 * Sam picked a line in each — the **anchor** — that has to land at a fixed
 * fraction of the viewport, so the horizon reads the same on every screen: a
 * third of the way down on a phone, and at the golden section on a desktop.
 *
 * The image is drawn at 100% of the viewport *width*, so everything positioned
 * against it is measured in `vw` too (`anchorOffset` below). Scaling to the
 * height instead would slide the skyline up and down with the browser chrome.
 */

export interface Backdrop {
  src: string;
  width: number;
  height: number;
  /** The anchor line's row, as a fraction of the image's height. */
  anchorFrac: number;
  /** Where that row must land, as a fraction of the viewport's height. */
  anchorPct: number;
  /** Where the picture stops carrying off-black ink, as a fraction of its height. */
  gradientFrac: number;
  /** Where it is fully too dark for it, and the header's ink is parchment. */
  flatFrac: number;
}

/** The colour both files end on, and the page's own ground below the image. */
export const GROUND = '#3d4327';

/**
 * The ink and the papers the cards on this background are made of. The alphas
 * are sampled off the Sept 19 mockups rather than guessed: the photograph has
 * to read through a card without the off-black text on it ever going grey.
 */
export const CARD_INK = '#362f2a';
export const CARD_BG = 'rgba(243, 238, 231, 0.80)';
/** The faint panel over dark grass: the phone's "Stats for my week:". */
export const PANEL_BG = 'rgba(243, 238, 231, 0.13)';
/** The sign-up questions' card, which carries more text and so more paper. */
export const QUESTION_BG = 'rgba(243, 238, 231, 0.91)';

/**
 * Measured to the pixel off the two ALIGNMENT REF files, whose blacked-out
 * region starts on the anchor row: mobile y = 1198 of 4385, desktop y = 1205
 * of 3631.
 *
 * `gradientFrac` and `flatFrac` are where the header's ink crosses, and they
 * are **not** the rows where each file reaches its flat `GROUND` (0.4479→0.5950
 * and 0.3757→0.5779, per the Sept 19 prompt). They are earlier, because the
 * picture stops carrying off-black long before it goes flat: sampled row by
 * row, mean luminance falls from 240 to about 100 across 0.21→0.30 on the
 * phone's file and 0.21→0.35 on the laptop's — the sky giving way to grass.
 * Crossing at the prompt's rows leaves off-black on dark green for most of a
 * scroll. The prompt invites the nudge; this is it, and it is a big one.
 */
export const MOBILE_BACKDROP: Backdrop = {
  src: '/backgrounds/account-mobile.jpg',
  width: 1152,
  height: 4385,
  anchorFrac: 0.273204,
  anchorPct: 1 / 3,
  gradientFrac: 0.21,
  flatFrac: 0.3,
};

export const DESKTOP_BACKDROP: Backdrop = {
  src: '/backgrounds/account-desktop.jpg',
  width: 3054,
  height: 3631,
  anchorFrac: 0.331865,
  anchorPct: 0.618034,
  gradientFrac: 0.21,
  flatFrac: 0.35,
};

/**
 * Which file a viewport gets — by *width*, at the repo's 900px breakpoint, not
 * by device. Sam framed the rule as "mobile devices and very narrow desktop
 * widths", and a 3054px-wide landscape photo in a 500px window is a smear.
 * The page *content* picks its layout the other way, off `usePhoneChrome()`.
 */
export const BACKDROP_BREAKPOINT = 900;

export const backdropFor = (vw: number): Backdrop =>
  vw < BACKDROP_BREAKPOINT ? MOBILE_BACKDROP : DESKTOP_BACKDROP;

/**
 * How far the image has to be scaled past 100% of the viewport width to keep
 * its top edge at or above the viewport's.
 *
 * Drawn at 100% width, the image's top sits at `anchorPct·vh − anchorFrac·H`,
 * which goes positive — a band of nothing above the picture — once the window
 * is tall for its width. The desktop rule holds to an aspect ratio of 1.566;
 * the mobile rule holds to 3.12:1, well past any phone, so in practice only a
 * short-and-wide desktop window ever scales. Scaling stays horizontally
 * centred, so the extra width is cropped evenly off both sides.
 */
export function backdropScale(b: Backdrop, vw: number, vh: number): number {
  const drawn = b.anchorFrac * (b.height / b.width) * vw;
  return Math.max(1, (b.anchorPct * vh) / drawn);
}

export interface BackdropBox {
  /** The image's drawn width and height, in px. */
  width: number;
  height: number;
  /** The image's top edge, as an offset from the top of the document. */
  top: number;
  /** Where the anchor line lands, from the top of the document. */
  anchor: number;
  /** Where the page's first object goes, which is the one rule that never bends. */
  firstTop: number;
}

export function backdropBox(b: Backdrop, vw: number, vh: number): BackdropBox {
  const scale = backdropScale(b, vw, vh);
  const width = vw * scale;
  const height = width * (b.height / b.width);
  const anchor = b.anchorPct * vh;
  return {
    width,
    height,
    top: anchor - b.anchorFrac * height,
    anchor,
    firstTop: anchor + (firstObjectVw(b) / 100) * vw,
  };
}

/**
 * The first object on the page — the account card, and the first event card of
 * a shared schedule — sits 21 CSS px below the anchor in both mockups. As a
 * fraction of the frame each was drawn in: 21/375 on the phone, 21/1728 on the
 * laptop. It is expressed in `vw` rather than `px` because the picture behind
 * it scales with the viewport's width, and an object that must stay on the
 * same patch of grass has to scale the same way.
 *
 * Note this is **not** the `-4.83vw` the Sept 19 prompt gives for desktop.
 * Re-measured against the mockup — whose background is drawn at exactly the
 * 61.8034% rule, confirmed by correlating its rows against the source file —
 * the card's top edge is at CSS y 599 with the anchor at 578.5, so it is 21px
 * *below* the anchor, as on the phone, not 83px above it.
 */
export const FIRST_OBJECT_VW = { mobile: 5.6, desktop: (21 / 1728) * 100 };

export const firstObjectVw = (b: Backdrop): number =>
  b === MOBILE_BACKDROP ? FIRST_OBJECT_VW.mobile : FIRST_OBJECT_VW.desktop;

/** Off-black, and parchment: what the header's ink crosses between. */
const INK_DARK = [0x36, 0x2f, 0x2a];
const INK_LIGHT = [0xf3, 0xee, 0xe7];

/**
 * Crosses fast through the middle and settles at each end.
 *
 * Somewhere in any fade the ink is mid-grey, and for a few rows the picture
 * behind it is mid-grey too; there is no threshold that avoids that, only a
 * curve that spends less time there. This is the usual smoothstep, which is
 * 1.5x the linear rate at the crossing point and flat at both ends.
 */
const smoothstep = (t: number) => t * t * (3 - 2 * t);

export const inkAt = (progress: number): string => {
  const t = smoothstep(Math.max(0, Math.min(1, progress)));
  const mix = INK_DARK.map((from, i) => Math.round(from + (INK_LIGHT[i] - from) * t));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
};

/**
 * How far the flat green has come up behind the header, 0 to 1.
 *
 * `headerMid` is the vertical centre of the header's own contents; `scrollY`
 * is how far the page has scrolled, which is what carries the gradient up.
 * Off-black stops reading against `GROUND`, so the ink crosses to parchment
 * over exactly the rows where the picture crosses to it.
 */
export function inkProgress(b: Backdrop, box: BackdropBox, headerMid: number, scrollY: number): number {
  const span = (b.flatFrac - b.gradientFrac) * box.height;
  if (span <= 0) return 0;
  const gradientLine = box.top + b.gradientFrac * box.height - scrollY;
  return Math.max(0, Math.min(1, (headerMid - gradientLine) / span));
}
